/**
 * 偏好设置-字体和字号设置
 * 用于编辑器和其他地方
 * 编辑器：UEditor 是iframe嵌入的形式，所以需要通过iframe的contentWindow来设置样式 编辑器内需要在[ueditorReady]内调用
 * 其他地方：直接设置样式
 * 提供两种调用方式
 * selector或者el为必传参数；config为对象，对象包含fontSize和fontFamily
 * selector--选择器 建议使用数组格式的selector
 * el-- 编辑器组件的节点 比如 this.$refs['editor'].$el
 * config-- 偏好设置 传值为对象 对象包含fontSize和fontFamily
 *
 * todo yqCustomSettingFontFamily 处理
 */
/**
 * dom中有两个 自定义的 style标签 一个是NationalFontSettingStyle 一个是fontSettingStyle
 * NationalFontSettingStyle 用于放置在线字体包
 * fontSettingStyle 用于设置font-family和font-size 以及一些其他的字体样式
 */

/**
 * @name StorageName
 * @description 偏好设置-字体和字号设置 存储的key
 */
const StorageName = {
  preference: 'YQ_PreferenceFontSetting',
  national: 'YQ_NationFontSetting'
}

const defaultFontSize = ''
const defaultFontFamily = ''

/**
 * @name getFontConfig
 * @public
 * @description 获取字体配置 从localStorage中获取
 * @param {string} type 类型 民族文或偏好字体 ['national'-民族文 | 'preference'-偏好字体]
 * @returns {object} 字体配置
 */
const getFontConfig = (type = 'preference') => {
  let config = localStorage.getItem(StorageName[type])
  try {
    if (!config) {
      return false
    }
    return { type, ...JSON.parse(config) }
  } catch (error) {
    console.log('error:', error)
    return false
  }
}

/**
 * @name setFontConfig
 * @public
 * @description 设置字体配置
 * @param {object} config 字体设置
 * @param {string} config.nationalTheme 民族文字体设置的字体包主题
 * @param {string} config.fontUrl 民族文字体设置的字体包在线地址
 * @param {string} config.fontFamily 字体设置的字体-family
 * @param {string} config.fontSize 字体设置的字体-size
 * @param {boolean} config.customSetting 字体设置的自定义字体开关
 */
const setFontConfig = (config) => {
  let code = config?.nationalTheme || config?.code
  let type = code ? 'national' : 'preference'
  if (code === 'default') {
    // 特殊场景【民族文】切换默认字体主题---如果有设置偏好字体则显示偏好字体，这里仅做一个清除民族文的操作
    setFontConfigInStorage('national') // 这里有个清空的操作，比如用户选择了默认主题
  } else if (type === 'national') {
    // 【民族文】切换民族文字体主题
    setFontConfigInStorage(type, {
      fontFamily: code,
      fontName: config.fontName,
      fontUrl: config.fontUrl,
      fontSize: config.fontSize
    })
    // 在设置民族文的字体配置时，如果偏好设置也有设置，需要同时保存在localStorage 以便民族文切换到默认主题后 可以恢复偏好字体设置
    if (config.customSetting && (config.fontFamily || config.fontSize)) {
      // 一般是登录的时候调用
      setFontConfigInStorage('preference', config)
    }
  } else if (type === 'preference') {
    // 设置偏好设置 自定义字体
    setFontConfigInStorage(type, config)
  } else {
    setFontConfigInStorage('national') // 这里有个清空的操作，比如用户选择了默认主题
    setFontConfigInStorage(type, config)
  }
}

/**
 * @name setFontClass
 * @public
 * @description 通过给body一个class 去控制民族文主题的布局
 * @param {string} code 民族文的code 唯一标识
 */
const setFontClass = (fontCode) => {
  try {
    let _body = document.getElementsByTagName('body')[0]
    if (_body && fontCode && !_body.classList.contains(fontCode + '-box')) {
      _body.classList.add(fontCode + '-box')
    }
  } catch (error) {
    console.log(error)
  }
}

/**
 * @name setCustomFontStyle
 * @public
 * @description 设置字体样式 可传入el和selector 二者必选其一
 * @param {object} param0 参数
 * @param {dom} el 节点 和selector 二选一 编辑器必须传入el
 * @param {string} elType 类型 编辑器传入'ueditor'
 * @param {Array[String]} selector 选择器 和el二选一 传入选择器 传入Array 或者 String 【民族文&偏好字体】用
 * @param {Array[String]} nationalSelector 同selector 仅【民族文】用 用于单独只针对于民族文使用而偏好字体不适用的情况
 * @param {Array[String]} preferenceSelector 同selector 仅【偏好字体】用 用于单独只针对于偏好字体使用而民族文不适用的情况
 * @param {string} types 设置的字体样式类型 传入Array 可以传空数组 也可以传入['fontSize', 'fontFamily']中的任意值。比如：['fontSize'] 或者 ['fontFamily']，['fontSize']表示只设置字体大小，缺省传入['fontSize', 'fontFamily']
 * @param {object} defaultConfig 接口获取的默认配置 传入对象 对象包含fontSize和fontFamily
 * @param {string} elseCss 其他样式 格式类似于"body{color:red;}"
 * @param {object} config 配置 传入对象 对象包含fontSize和fontFamily
 * @param {String} config.type 类型 民族文或偏好字体 ['national'-民族文 | 'preference'-偏好字体]
 * @param {string} insertStyleType 插入样式类型 append | cover 直接在后面补充|覆盖该style
 * @returns {string} 最终插入的样式文本
 */
const setCustomFontStyle = (
  {
    elType,
    el,
    selector,
    nationalSelector,
    preferenceSelector,
    types = ['fontSize', 'fontFamily'],
    defaultConfig,
    elseCss = ''
  },
  config,
  insertStyleType
) => {
  let resStyleText = ''
  if (config && typeof config === 'object') {
    let fontStyle = {}
    if (types.includes('fontSize')) {
      fontStyle.fontSize =
        config.fontSize || defaultConfig.fontSize || defaultFontSize
    }
    if (types.includes('fontFamily')) {
      fontStyle.fontFamily =
        config.fontFamily || defaultConfig.fontFamily || defaultFontFamily
    }
    let styleText = '' // 需要插入的样式文本
    let needElseStyle =
      config.fontFamily &&
      (config.fontFamily === 'chars-weihakowen' ||
        config.fontFamily === 'FZWWZTOT_Uni')
    // 给民族文加个字体important 防止被覆盖
    if (config.type === 'national') {
      fontStyle.fontFamily = config.fontFamily
      fontStyle.type = config.type
    }
    if (elseCss && typeof elseCss === 'string') {
      styleText += elseCss
    }
    if (el && elType === 'ueditor') {
      let selectorStr = 'body.view'
      if (needElseStyle) {
        styleText =
          `${selectorStr}{writing-mode: horizontal-tb;direction: rtl;unicode-bidi: plaintext;}` +
          styleText
      }
      resStyleText = setUEditorCustomFontStyle(el, fontStyle, styleText)
    } else if (
      effectiveArray(selector) ||
      effectiveArray(nationalSelector) ||
      effectiveArray(preferenceSelector)
    ) {
      let _selectors = []
      if (effectiveArray(selector)) {
        _selectors = selector
      }
      if (effectiveArray(nationalSelector) && config.type === 'national') {
        _selectors = _selectors.concat(nationalSelector)
      }
      if (effectiveArray(preferenceSelector) && config.type === 'preference') {
        _selectors = _selectors.concat(preferenceSelector)
      }
      if (_selectors.length > 0) {
        let selectorStr = ''
        selectorStr = _selectors.join(',')
        if (needElseStyle) {
          styleText =
            `${selectorStr}{direction: rtl;word-break: break-word;}` + styleText
        }
        resStyleText = setOtherCustomFontStyle(
          selectorStr,
          fontStyle,
          styleText,
          insertStyleType
        )
      }
    } else if (el) {
      if (needElseStyle) {
        styleText = 'direction: rtl;word-break: break-word;text-align: justify;'
      }
      resStyleText = setFontStyleByEl(el, fontStyle, styleText)
    }
  }
  return resStyleText
}

/**
 * @name setFontFace
 * @public
 * @description 设置在线字体包 将字体包设置到id为[NationalFontSettingStyle]的style标签
 * @param {object} fontStyle 字体设置
 * @param {string} fontStyle.fontUrl 字体包在线地址
 * @param {string} fontStyle.fontFamily 字体code
 */
const setFontFace = (fontStyle) => {
  if (fontStyle?.fontUrl && fontStyle?.fontFamily) {
    const idName = 'NationalFontSettingStyle'
    let styleEl = document.getElementById(idName)
    if (styleEl && styleEl.getAttribute('data-code') === fontStyle.fontFamily) {
      return
    }
    let styleText = `@font-face {
      font-family: ${fontStyle.fontFamily};
      src: url("${fontStyle.fontUrl}");
      font-weight: normal;
      font-size: normal;
    }`
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = idName
      styleEl.innerHTML = styleText
      document.head.appendChild(styleEl)
    } else {
      styleEl.innerHTML = styleText
    }
  }
}

/**
 * @name setFontConfigInStorage
 * @description 设置字体包主题到localStorage
 * @param {string} type 字体包主题
 * @param {object} config 字体包在线地址
 */
const setFontConfigInStorage = (type, config) => {
  try {
    let setting = {}
    let storageName = StorageName[type]
    if (type == 'national') {
      if (config && config.fontFamily && config.fontUrl) {
        if (config.fontFamily) {
          setting.fontFamily = config.fontFamily
        }
        if (config.fontUrl) {
          setting.fontUrl = config.fontUrl
        }
        if (config.fontSize) {
          setting.fontSize = config.fontSize
        }
        if (config.fontName) {
          setting.fontName = config.fontName
        }
        localStorage.setItem(storageName, JSON.stringify(setting))
      } else {
        localStorage.removeItem(storageName)
      }
    } else if (type === 'preference') {
      if (config?.customSetting && (config.fontFamily || config.fontSize)) {
        // 【字体设置】 开启了自定义字体设置开关，才会生效
        if (config.fontFamily) {
          setting.fontFamily = config.fontFamily
        }
        if (config.fontSize) {
          setting.fontSize = config.fontSize
        }
        localStorage.setItem(storageName, JSON.stringify(setting))
      } else {
        localStorage.removeItem(storageName)
      }
    } else if (type !== 'national' && type !== 'preference') {
      console.log('type错误')
      return
    }
  } catch (error) {
    console.log('error:', error)
  }
}

/**
 * 设置UEditor的字体样式
 * @param {*} el 节点
 * @param {*} config 配置
 * @param {*} elseStyle 额外的一些样式
 */
const setUEditorCustomFontStyle = (el, config, elseStyle = '') => {
  if (el) {
    let iframe = el.querySelector('iframe')
    if (iframe && iframe.contentWindow) {
      let selector = 'body.view'
      if (config.type === 'national') {
        selector = 'body.view *'
      }
      return insertFontStyle(
        selector,
        config,
        iframe.contentWindow.document,
        elseStyle,
        'cover'
      )
    }
  }
}
/**
 * 设置选择器的字体样式
 * @param {*} selector 选择器
 * @param {*} config 配置
 * @param {*} elseStyle 额外的一些样式
 * @param {*} insertStyleType 插入样式类型 append | cover
 * @returns {string} 最终插入的样式文本
 */
const setOtherCustomFontStyle = (
  selector,
  config,
  elseStyle = '',
  insertStyleType
) => {
  return insertFontStyle(selector, config, document, elseStyle, insertStyleType)
}
/**
 * @name insertFontStyle
 * @description 字体样式插入到dom中 id为[fontSettingStyle]的style标签
 * @param {*} selector 选择器
 * @param {*} config 配置
 * @param {*} currDoc 作用的dom
 * @param {*} elseStyle 额外的一些样式
 * @param {*} insertStyleType 插入样式类型 append | cover
 * @returns {string} 最终插入的样式文本
 */
const insertFontStyle = (
  selector,
  config,
  currDoc = document,
  elseStyle = '',
  insertStyleType = 'append'
) => {
  const idName = 'fontSettingStyle'
  let styleEl = currDoc.getElementById(idName)
  let styleText = ''
  if (selector) {
    // 追加样式避免丢失
    styleText = `${selector} {`
    if (config.fontSize) {
      styleText += `font-size: ${config.fontSize}px;`
    }
    if (config.fontFamily) {
      styleText += `font-family: ${config.fontFamily};`
    }
    styleText += `}`
  }
  let style = styleText + elseStyle
  if (!styleEl) {
    styleEl = currDoc.createElement('style')
    styleEl.id = idName
    styleEl.innerHTML = style
    currDoc.head.appendChild(styleEl)
  } else {
    if (insertStyleType === 'append') {
      styleEl.innerHTML += style
    } else if (insertStyleType === 'cover') {
      styleEl.innerHTML = style
    }
  }
  return style
}
/**
 * @name setFontStyleByEl
 * @description 设置元素的字体样式
 * @param {*} el 元素
 * @param {*} config 配置
 * @param {*} elseStyle 额外的一些样式
 * @returns {string} 最终插入的样式文本
 */
const setFontStyleByEl = (el, config, elseStyle) => {
  const breakId = ['tuwenText']
  let style = ''
  if (el && config && typeof config === 'object') {
    if (config.fontSize) {
      el.style.fontSize = config.fontSize + 'px'
    }
    if (config.fontFamily) {
      el.style.fontFamily = config.fontFamily
    }
    if (typeof elseStyle !== 'string') {
      elseStyle = ''
    }
    let _style = el.getAttribute('style') || ''
    if (breakId.includes(el.id)) {
      elseStyle += 'word-break:break-world;'
    }
    style = _style + elseStyle
    el.setAttribute('style', style)
  }
  return style
}
const effectiveArray = (arr) => {
  return Array.isArray(arr) && arr.length > 0
}

export default {
  getFontConfig,
  setFontConfig,
  setFontClass,
  setCustomFontStyle,
  setFontFace
}
